import { oauthSetupSteps } from "../oauthSetupSteps";
import { mcpConnectionTest } from "../mcpConnectionTest";
import { bearerAuth, oauthAuth, dcrOAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import logo from "./logo.svg";

export const cloudflare = defineProduct({
  name: "Cloudflare",
  tagLine: "Web performance and security",
  keywords: "cdn dns waf ddos zones workers zero trust",
  logo,
  tags: ["security", "engineering"],
  variants: [
    productVariant({
      identifier: "cloudflare",
      authLabel: "API token",
      protocol: "http",
      defaultUrlPattern: () => "https://api\\.cloudflare\\.com/client/v4/.*",
      auth: (app) => bearerAuth(app.secret.apiToken),
      steps: [
        {
          title: "Get your Cloudflare API token",
          items: [
            {
              kind: "markdown",
              content:
                "Create a [user API token](https://developers.cloudflare.com/fundamentals/api/get-started/create-token/) in **My Profile > API Tokens**, or an [account-owned token](https://developers.cloudflare.com/fundamentals/api/get-started/account-owned-tokens/) in **Manage account > Account API tokens**. Grant only the permissions and resources you need. Creating account-owned tokens requires API Token Provisioning capabilities or Super Administrator status.",
            },
          ],
        },
        {
          title: "Enter your API token",
          items: [
            {
              kind: "input",
              label: "API token",
              required: true,
              target: { kind: "secrets", field: "apiToken" },
              helperText:
                "Used for Cloudflare’s /client/v4/ API, including GraphQL Analytics, not the R2 S3 API.",
            },
            {
              kind: "input",
              label: "Account ID",
              target: { kind: "configuration", field: "accountId" },
              helperText:
                "Required for an account-owned token; leave blank for a user token. Enter the 32-character ID of the account that owns the token. In the Cloudflare dashboard, open Search and select Copy account ID.",
            },
          ],
        },
      ],
      connectionTest: {
        protocol: "http",
        request: ({ accountId }) => ({
          url: accountId?.trim()
            ? `https://api.cloudflare.com/client/v4/accounts/${encodeURIComponent(accountId.trim())}/tokens/verify`
            : "https://api.cloudflare.com/client/v4/user/tokens/verify",
          method: "GET",
          expectedStatus: 200,
          expectedBodyContains: '"active"',
        }),
      },
    }),

    productVariant({
      identifier: "cloudflareMcp",
      authLabel: "MCP OAuth",
      protocol: "http",
      defaultUrlPattern: () => "https://mcp\\.cloudflare\\.com/mcp(?:\\?.*)?$",
      auth: () => oauthAuth,
      oauth: dcrOAuth({
        serverUrl: "https://mcp.cloudflare.com/mcp",
        issuer: "https://mcp.cloudflare.com",
        authorizationEndpoint: "https://mcp.cloudflare.com/authorize",
        tokenEndpoint: "https://mcp.cloudflare.com/token",
        resource: "https://mcp.cloudflare.com/mcp",
        scopes: ["user:read", "account:read", "offline_access"],
      }),
      steps: [
        {
          title: "Connect to Cloudflare",
          items: [
            {
              kind: "markdown",
              content:
                "Sign in to Cloudflare and approve the permissions you want to grant. This connection uses Cloudflare’s MCP server. For direct REST API requests, use the API token or OAuth connection.",
            },
            {
              kind: "input",
              label: "Scopes",
              helperText:
                "Optional space-separated Cloudflare permissions. The default grants account and user read access. Include offline_access for background access.",
              target: { kind: "oauthConnection", field: "scopes" },
            },
            { kind: "oauth" },
          ],
        },
      ],
      connectionTest: mcpConnectionTest("https://mcp.cloudflare.com/mcp"),
      usageHint: () =>
        "Call Cloudflare through streamable HTTP MCP at https://mcp.cloudflare.com/mcp. Load the mcp-client skill for the handshake. The search, execute, and docs tools provide access to Cloudflare APIs and documentation within the permissions approved during connection.",
    }),
    productVariant({
      identifier: "cloudflareOAuth",
      authLabel: "OAuth",
      protocol: "http",
      defaultUrlPattern: () => "https://api\\.cloudflare\\.com/client/v4/.*",
      auth: oauthAuth,
      steps: oauthSetupSteps({
        identifier: "cloudflareOAuth",
        registrationUrl: "https://dash.cloudflare.com/",
        scopes: ["user-details.read", "offline_access"],
        instructions:
          "In Manage Account > OAuth clients, configure authorization code and refresh token grants with client_secret_basic authentication. Select User Details Read and your workflow’s API permissions. New clients can be authorized only by members of the owning account until promoted to public visibility.",
      }),
      oauth: {
        grantType: () => "authorization_code",
        authorizationEndpoint: () => "https://dash.cloudflare.com/oauth2/auth",
        tokenEndpoint: () => "https://dash.cloudflare.com/oauth2/token",
        tokenEndpointAuthMethod: () => "client_secret_basic",
        clientCredentialsFrom: "db",
        scopes: () => ["user-details.read", "offline_access"],
        pkce: () => true,
      },
      connectionTest: {
        protocol: "http",
        request: () => "https://api.cloudflare.com/client/v4/user",
      },
    }),
  ],
});
